import React, { useState, useEffect, useCallback } from "react";
import DashboardHeader from "../../components/DashboardHeader";
import Sidebar from "../../components/Sidebar";
import "./AccountantEvents.css";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  createEventApi,
  listEventsApi,
  updateEventApi,
  deleteEventApi,
} from "../../services/apiCall";
import EventFilters from "./EventFilters";
import EventCard from "./EventCard";
import AddEventModal from "./AddEventModal";

const AccountantEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    eventType: "reminder",
    category: "",
    timeline: "upcoming",
    status: "todo",
    priority: "high",
    dueDate: "",
    reminders: [],
    metadata: {},
    attachments: [],
  });
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const initialFilters = {
    status: "",
    priority: "",
    eventType: "",
    category: "",
    startDate: "",
    endDate: "",
  };
  const [filters, setFilters] = useState(initialFilters);

  const userId = useSelector((state) => state?.user?.userId);
  const businessId = useSelector((state) => state?.user?.businessId);
  const [showFilters, setShowFilters] = useState(true);
  const [tempFilters, setTempFilters] = useState(filters);

  const applyFilters = () => {
    setFilters(tempFilters);
  };

  const clearFilters = () => {
    setTempFilters(initialFilters);
    setFilters(initialFilters);
  };

  const fetchEvents = useCallback(async () => {
    if (!businessId) return;

    setLoading(true);
    try {
      const response = await listEventsApi(businessId, filters);
      const eventsData = response?.data || response || [];
      setEvents(eventsData);
    } catch (err) {
      console.error("Failed to load events:", err);
      toast.error("Could not load events. Please try again.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [businessId, filters]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const openModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        title: event.title || "",
        description: event.description || "",
        eventType: event.eventType || "reminder",
        category: event.category || "",
        timeline: event.timeline || "upcoming",
        status: event.status || "todo",
        priority: event.priority || "high",
        dueDate: event.dueDate?.split("T")[0] || "",
        reminders: event.reminders || [],
        metadata: event.metadata || {},
        attachments: [],
      });
    } else {
      setEditingEvent(null);
      setFormData({
        title: "",
        description: "",
        eventType: "reminder",
        category: "",
        timeline: "upcoming",
        status: "todo",
        priority: "high",
        dueDate: "",
        reminders: [],
        metadata: {},
        attachments: [],
      });
    }
    setErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingEvent(null);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.dueDate) newErrors.dueDate = "Due date is required";
    else if (new Date(formData.dueDate) < new Date().setHours(0, 0, 0, 0))
      newErrors.dueDate = "Due date must be today or in the future";
    if (!formData.category.trim()) newErrors.category = "Category is required";

    formData.reminders.forEach((rem, idx) => {
      if (!rem.reminderDate)
        newErrors[`reminderDate_${idx}`] = "Reminder date required";
      if (!rem.notes?.trim()) newErrors[`notes_${idx}`] = "Notes required";
    });

    formData.attachments.forEach((file, idx) => {
      if (file.size > 5 * 1024 * 1024)
        newErrors[`attachment_${idx}`] = "File too large (max 5MB)";
      if (
        ![
          "application/pdf",
          "image/jpeg",
          "image/png",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ].includes(file.type)
      )
        newErrors[`attachment_${idx}`] = "Unsupported file type";
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.warning("Please correct the errors before submitting.");
      return;
    }

    setSaving(true);

    const payload = new FormData();
    payload.append("businessId", businessId);
    payload.append("createdBy", userId);
    payload.append("title", formData.title.trim());
    payload.append("description", formData.description);
    payload.append("eventType", formData.eventType);
    payload.append("category", formData.category.trim());
    payload.append("timeline", formData.timeline);
    payload.append("status", formData.status);
    payload.append("priority", formData.priority);
    payload.append("dueDate", formData.dueDate);
    payload.append("metadata", JSON.stringify(formData.metadata));
    formData.attachments.forEach((file) => payload.append("files", file));

    try {
      if (editingEvent) {
        await updateEventApi(editingEvent.id, payload);
        toast.success("Event updated successfully!");
      } else {
        await createEventApi(payload);
        toast.success("Event created successfully!");
      }

      await fetchEvents();
      closeModal();
    } catch (err) {
      console.error(err);
      toast.error("Operation failed: " + (err.message || "Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    setLoading(true);
    try {
      await deleteEventApi(id);
      await fetchEvents();
      toast.success("Event deleted successfully!");
    } catch (err) {
      toast.error("Delete failed: " + (err.message || "Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const addReminder = () => {
    setFormData({
      ...formData,
      reminders: [...formData.reminders, { reminderDate: "", notes: "" }],
    });
  };

  const removeReminder = (idx) => {
    setFormData({
      ...formData,
      reminders: formData.reminders.filter((_, i) => i !== idx),
    });
  };

  const updateReminder = (idx, field, value) => {
    const updated = [...formData.reminders];
    updated[idx][field] = value;
    setFormData({ ...formData, reminders: updated });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, attachments: Array.from(e.target.files) });
  };

  const activeFilterCount = Object.values(filters).filter(
    (value) => value !== "" && value !== null,
  ).length;

  const filteredAndSortedEvents = [...events]
    .filter((ev) =>
      ev?.title?.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));

  const formatDate = (dateStr) => {
    if (!dateStr) return "No date";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="app">
      <Sidebar />
      <main className="main">
        <DashboardHeader title="Events" />

        <div className="events-container">
          <div className="events-header">
            <div>
              <h2>Important Dates & Reminders</h2>
              <p className="subtitle">
                Manage your accounting schedule efficiently
              </p>
            </div>
            <div className="header-actions">
              <div className="search-box">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
              <button className="add-event-btn" onClick={() => openModal()}>
                <span className="plus-icon">+</span> Add Event
              </button>
            </div>
          </div>

          <EventFilters
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            activeFilterCount={activeFilterCount}
            tempFilters={tempFilters}
            setTempFilters={setTempFilters}
            applyFilters={applyFilters}
            clearFilters={clearFilters}
          />

          {loading ? (
            <div className="loader-container">
              <div className="spinner"></div>
              <p>Loading your calendar...</p>
            </div>
          ) : filteredAndSortedEvents.length === 0 ? (
            <div className="no-events-state">
              <div className="no-events-icon">🗓️</div>
              <h3>No Events Found</h3>
              <p>
                {events.length === 0
                  ? "You don't have any events scheduled yet. Create one to get started."
                  : "No events match your current search constraints. Try adjusting them."}
              </p>
              {events.length === 0 && (
                <button
                  className="add-event-btn mt-4 inline-flex"
                  onClick={() => openModal()}
                >
                  + Create First Event
                </button>
              )}
            </div>
          ) : (
            <div className="events-grid">
              {filteredAndSortedEvents.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  openModal={openModal}
                  handleDelete={handleDelete}
                  formatDate={formatDate}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {isModalOpen && (
        <AddEventModal
          closeModal={closeModal}
          formData={formData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          errors={errors}
          editingEvent={editingEvent}
          saving={saving}
          updateReminder={updateReminder}
          removeReminder={removeReminder}
          addReminder={addReminder}
          handleFileChange={handleFileChange}
        />
      )}
    </div>
  );
};

export default AccountantEvents;
